import React, { FC, useContext, useEffect, useState } from 'react';

import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Typography,
  Card,
  CardHeader,
  CardContent,
  LinearProgress,
  Paper,
  Divider,
} from '@material-ui/core';
import Pagination from '@material-ui/lab/Pagination';

//components
import List from './components/List';
import Modal from '../../components/Modal';
import Add from './components/Add';
import Can from '../../components/Can';

//helpers
import { removeEmptyValueObject, checkMatchMedia } from '../../utils/helpers';
import useQuery from '../../utils/hook/useQuery';
import windowSize from '../../utils/hook/useWindowResize';

// redux
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { Store } from '../../redux/reducers';
import {
  makeSelectSellersList,
  makeSelectCountList,
  makeSelectError,
  makeSelectLoading,
  makeSelectActiveCardCountList,
  makeSelectInactiveCardCountList,
} from './selectors';
import {
  createSellerAction,
  getSellersAction,
  updateSellerStatus,
} from './actions';

// types and interfaces
import { addSeller, Payload, PayloadCreate, Props, State } from './interfaces';

// context api
import AuthContext from '../../authContext';
import { Active, Inactive } from '../sellers/customIcon';
import RonadIcons from '../../components/iconComponent';

type Inputs = {
  'name.contains': string;
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {},
    paper: {},
    loading: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      height: '50vh',
      alignSelf: 'center',
      justifyContent: 'center',
      background: '#ccc',
    },
    textArea: {
      width: '100%',
    },
    searchBox: {
      display: 'flex',
      position: 'relative',
      padding: '10px 0',
      backgroundColor: theme.palette.common.white,
      borderRadius: '4px',
      boxShadow: '0px 2px 4px 1px rgba(0,0,0,0.1)',
    },

    input: {
      marginLeft: theme.spacing(1),
      flex: 2,
      '@media (max-width: 768px)': {
        fontSize: '12px',
      },
    },
    iconButton: {
      padding: 10,
      position: 'relative',
      marginRight: '10px',
      '&:before': {
        content: '""',
        position: 'absolute',
        width: '1px',
        height: '36px',
        top: '4px',
        left: '-9px',
        backgroundColor: '#EDF1F4',
      },
    },
    cards: {
      position: 'relative',
      backgroundColor: 'white',
      padding: '14px',
    },
    boxshadow: {
      position: 'relative',
      backgroundColor: 'white',
      padding: '14px',
      transform: 'translateY(-3px)',
      boxShadow: '-4px 4px 12px 0px rgba(0,0,0,0.15) !important',
    },
    iconHeader: {
      position: 'absolute',
      top: '5px',
      right: '5px',
    },
    cardContent: {
      marginTop: '-22px',
      display: 'flex',
    },
    hover: {
      '&:hover': {
        curser: 'pointer',
      },
    },
    chart: {
      padding: '24px',
      height: '312px',
      backgroundColor: 'white',
      width: '100%',
      overflow: 'visible',
    },
    progress: {
      padding: '24px',
      height: '312px',
      backgroundColor: 'white',
      width: '100%',
      overflow: 'visible',
    },

    filterBox: {
      position: 'absolute',
      backgroundColor: '#fff',
      width: '100%',
      top: '47px',
      right: '0',
      marginTop: '30px',
      borderRadius: '0  0 4px  4px',
      padding: '16px 16px',
      boxSizing: 'border-box',
      boxShadow: '0px 2px 4px 1px rgba(0,0,0,0.1)',
      zIndex: 10,
    },

    filterOpen: {
      display: 'block',
    },
    filterClose: {
      display: 'none',
    },
    paginationBox: {
      display: 'flex',
    },
    selectSize: {
      width: '35px',
    },
    subtitle: {
      fontSize: '14px',
      fontWeight: 500,
      color: '#8A8A8A',
    },
    magnifyIcon: {
      transform: 'rotate(-90deg)',
    },
    cancelIcon: {
      textAlign: 'center',
      position: 'relative',
      paddingLeft: theme.spacing(8),
      marginBottom: theme.spacing(7),
      '&::before': {
        content: '""',
        position: 'absolute',
        width: '100px',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%,-50%)',
        height: '100px',
        borderRadius: '50%',
        opacity: '0.2',
        backgroundColor: theme.palette.error.main,
      },
    },
    cancelTitle: {
      marginTop: '45px',

      fontSize: '16px',
      fontWeight: 500,
      textAlign: 'center',
      marginBottom: theme.spacing(1),
    },
    actionsBox: {
      paddingTop: theme.spacing(2),
      paddingRight: theme.spacing(3),
      paddingLeft: theme.spacing(3),
    },
    editIcon: {
      textAlign: 'center',
      position: 'relative',
      paddingLeft: theme.spacing(8),
      // marginBottom: theme.spacing(7),
      '&::before': {
        content: '""',
        position: 'absolute',
        width: '100px',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%,-50%)',
        height: '100px',
        borderRadius: '50%',
        opacity: '0.2',
        backgroundColor: theme.palette.blue.main,
      },
    },
    addIcon: {
      textAlign: 'center',
      position: 'relative',
      paddingLeft: theme.spacing(8),
      '&::before': {
        content: '""',
        position: 'absolute',
        width: '100px',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%,-50%)',
        height: '100px',
        borderRadius: '50%',
        opacity: '0.2',
        backgroundColor: theme.palette.green.main,
      },
    },
    closeButton: {
      position: 'absolute',
      right: theme.spacing(1),
      top: theme.spacing(1),
      color: theme.palette.grey[500],
    },
    btnBox: {
      marginTop: '5px',
      padding: '10px',
      display: 'flex',
      flexDirection: 'row-reverse',
      width: '100%',
      '@media (max-width: 768px)': {
        display: 'flex',
        justifyContent: 'space-between',
        width: '100%',
      },
    },
    headerBox: {
      marginLeft: '75px',
      display: 'flex',
      alignItems: 'center',
      marginBottom: '10px',
      marginTop: '10px',
    },
    cardContentOrder: {
      padding: '0',
    },
    cardContentlist: {
      justifyContent: 'space-between',
      padding: ' 0',
    },
    typographyCards: {
      fontSize: '16px',
      color: 'white',
      backgroundColor: '#4BCF90',
      padding: '8px 24px 8px 50px',
      marginLeft: '-40px',
    },
    card: {
      width: '100%',
      marginBottom: '14px',
      overflow: 'visible',
      padding: '14px 0 0',
      paddingBottom: '0',
    },
  })
);

const Sellers: FC<Props> = (props: Props): React.ReactElement => {
  const authContext = useContext(AuthContext);
  const [openSize, setOpenSize] = useState(false as boolean);
  const [openConfirmDisable, setOpenConfirmDisable] = useState(
    false as boolean
  );
  const [openConfirmActive, setOpenConfirmActive] = useState(false as boolean);
  const [openAddModal, setOpenAddModal] = useState(false as boolean);
  const [id, setId] = useState(-1 as number);
  const [isMob, setIsMob] = useState(false);
  const { width } = windowSize();
  const { filtersValues, setFiltersValues } = useQuery({
    partnerId: { equals: localStorage.getItem('partnerId') },
    page: 0,

    size: 10,
  });
  const [formData, setFormData] = useState(null as null | addSeller);
  const [checkAddSeller, setCheckAddSeller] = useState(false as boolean);

  const classes = useStyles();

  useEffect(() => {
    getSellers();
  }, [filtersValues]);

  useEffect(() => {
    if (width && width <= 480) {
      setIsMob(true);
    } else {
      setIsMob(false);
    }
  }, [width]);

  const getSellers = () => {
    const { page, size } = filtersValues;
    props.sellersDispatch({
      ...removeEmptyValueObject(filtersValues),
      page,
      size,
    });
  };

  const handleClose = () => {
    setOpenConfirmDisable(false);
    setOpenConfirmActive(false);
    setOpenSize(false);
  };

  const handleChangeSizePage = (event: React.ChangeEvent<{ value: unknown }>) => {
    setFiltersValues({
      ...filtersValues,
      page: 0,
      size: Number(event.target.value),
    });
  };

  const handleChangePage = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setFiltersValues({ ...filtersValues, page: page - 1 });
  };

  const handleOpen = (id: number) => {
    setId(id);
  };

  const handleOpenConfirmDisable = (id: number) => {
    setOpenConfirmDisable(true);
    setId(id);
  };
  const handleOpenConfirmActive = (id: number) => {
    setOpenConfirmActive(true);
    setId(id);
  };

  const handleDisableSeller = async () => {
    await props.updateSellerStatusDispatch({
      id,
      body: 'INACTIVE'.replace(/'/g, '"'),
      pagination: filtersValues,
    });
    setOpenConfirmDisable(false);
  };

  const handleActiveSeller = async () => {
    await props.updateSellerStatusDispatch({
      id,
      body: 'ACTIVE'.replace(/'/g, '"'),
      pagination: filtersValues,
    });

    setOpenConfirmActive(false);
  };

  const handleOpenConfirmUpdateState = (data: { id: number; body: any }) => {
    setId(data.id);
  };

  const handleCreateSeller = (body: addSeller) => {
    setFormData(body);
    setCheckAddSeller(true);
  };

  const handleCardClose = () => {
    const { page, size } = filtersValues;
    delete filtersValues['status.in'];
    setFiltersValues({
      ...removeEmptyValueObject(filtersValues),

      page: 0,
      size: 10,
    });
  };

  const handleCardClick = (name: any) => {
    const { page, size } = filtersValues;

    switch (name) {
      case 'activeSeller':
        setFiltersValues({
          ...removeEmptyValueObject(filtersValues),
          'status.in': ['ACTIVE'],
          page: 0,
          size: 10,
        });
        break;
      case 'inactiveSeller':
        setFiltersValues({
          ...removeEmptyValueObject(filtersValues),
          'status.in': ['INACTIVE'],
          page: 0,
          size: 10,
        });
        break;
    }
  };
  const checkActiveCard = (name: string) => {
    const status = filtersValues['status.in']?.split(',') || [];
    switch (name) {
      case 'activeSeller':
        return status.includes('ACTIVE');
      case 'inactiveSeller':
        return status.includes('INACTIVE');
    }
  };

  return (
    <div className={classes.root}>
      <Typography variant="h6" component="h2">
        {'مدیریت فروشندگان'}
      </Typography>
      <Grid
        container
        spacing={2}
        md={12}
        style={{
          maxWidth: '120%',
          margin: '24px 0 24px 0',
        }}
      >
        <Grid container lg={12} spacing={2} xs={12}>
          <Grid item md={6} lg={6} xs={12}>
            <Card
              className={
                checkActiveCard('activeSeller')
                  ? classes.boxshadow
                  : classes.cards
              }
            >
              <CardHeader
                action={
                  checkActiveCard('activeSeller') && (
                    <IconButton
                      aria-label="close"
                      onClick={handleCardClose}
                      className={classes.iconHeader}
                    >
                      <RonadIcons name="times" size={0.8} className="text-danger" />
                    </IconButton>
                  )
                }
              />
              <CardContent className={classes.cardContent}>
                <IconButton
                  type="submit"
                  onClick={name => handleCardClick('activeSeller')}
                  aria-label="search"
                  style={{
                    backgroundColor: '#D6EBD3',
                    // height: '30%',
                    // width: '28%',
                    borderRadius: '4px',
                    margin: '-20px 0',
                  }}
                >
                  <Active />
                </IconButton>
                <Grid
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    marginRight: '10px',
                  }}
                >
                  <Typography
                    variant="h6"
                    component="h6"
                    style={{ fontWeight: 600, marginTop: '-20px' }}
                  >
                    {props.activeCardCount}
                  </Typography>
                  <Typography
                    style={{
                      fontSize: '16px',
                      marginBottom: '-20px',
                      fontWeight: 550,
                      color: '#666666',
                    }}
                  >
                    فروشنده فعال
                  </Typography>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          <Grid item md={6} lg={6} xs={12}>
            <Card
              className={
                checkActiveCard('inactiveSeller')
                  ? classes.boxshadow
                  : classes.cards
              }
            >
              <CardHeader
                action={
                  checkActiveCard('inactiveSeller') && (
                    <IconButton
                      aria-label="close"
                      onClick={handleCardClose}
                      className={classes.iconHeader}
                    >
                      <RonadIcons name="times" size={0.8} className="text-danger" />
                    </IconButton>
                  )
                }
              />
              <CardContent className={classes.cardContent}>
                <IconButton
                  type="submit"
                  aria-label="search"
                  onClick={name => handleCardClick('inactiveSeller')}
                  style={{
                    backgroundColor: '#FCD4DA',
                    // height: '60%',
                    // width: '30%',
                    borderRadius: '4px',
                    margin: '-20px 0',
                  }}
                >
                  <Inactive />
                </IconButton>
                <Grid
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    marginRight: '10px',
                  }}
                >
                  <Typography
                    variant="h6"
                    component="h6"
                    style={{ fontWeight: 600, marginTop: '-20px' }}
                  >
                    {props.inactiveCardCount}
                  </Typography>
                  <Typography
                    style={{
                      fontSize: '16px',
                      marginBottom: '-20px',
                      fontWeight: 550,
                      color: '#666666',
                    }}
                  >
                    فروشنده غیر فعال
                  </Typography>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Grid>
      <Grid container spacing={1}>
        <Card className={classes.card}>
          <CardHeader
            avatar={
              <Grid>
                <Typography className={classes.typographyCards}>
                  لیست فروشندگان
                </Typography>
              </Grid>
            }
            action={
              <div className={classes.btnBox}>
                <Can
                  role={authContext.user.role}
                  perform="sellers:add"
                  yes={() => (
                    <Button
                      variant="contained"
                      color="primary"
                      size={
                        checkMatchMedia('(max-width: 1024px)')
                          ? 'small'
                          : 'large'
                      }
                      startIcon={<RonadIcons name='plus' className='text-white' size={0.7}/>}
                      onClick={() => {
                        props.history.push('/sellerAdd');
                      }}
                    >
                      {'ایجاد فروشنده '}
                    </Button>
                  )}
                />
              </div>
            }
          />

          <CardContent className={classes.cardContentOrder}>
            <Grid container lg={12} md={12} className={classes.cardContentlist}>
              <Grid container item lg={12} md={12}>
                <Grid
                  container
                  item
                  xs={12}
                  md={12}
                  lg={12}
                  alignItems="center"
                >
                  {props.loading ? (
                    <div className={classes.loading}>
                      <CircularProgress />
                    </div>
                  ) : (
                    <>
                      {props.sellerList.length > 0 ? (
                        <List
                          data={props.sellerList}
                          changeStatus={handleOpenConfirmUpdateState}
                          handleOpenConfirmDisable={handleOpenConfirmDisable}
                          handleOpenConfirmActive={handleOpenConfirmActive}
                          handleOpen={handleOpen}
                        />
                      ) : (
                        <Box mt={4}>
                          <Typography variant="h3" component="div">
                            فروشنده‌ای موجود نیست
                          </Typography>
                        </Box>
                      )}
                    </>
                  )}
                </Grid>
                <Grid item xs={12} sm={6} style={{ padding: '20px 10px 0 10px' }}>
                  {props.sellerList.length > 0 && (
                    <Grid container>
                      <Grid item xs={12} sm={6}>
                        <FormControl
                          style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                          }}
                        >
                          <div style={{ marginLeft: '10px', color: '#8a8a8a' }}>
                            {' '}
                            تعداد نمایش در هر صفحه:
                          </div>
                          <Select
                            open={openSize}
                            onClose={handleClose}
                            onOpen={() => setOpenSize(true)}
                            value={filtersValues.size}
                            onChange={handleChangeSizePage}
                          >
                            <MenuItem value={10}>10</MenuItem>
                            <MenuItem value={20}>20</MenuItem>
                            <MenuItem value={50}>50</MenuItem>
                            <MenuItem value={100}>100</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  )}
                </Grid>
                <Grid
                  xs={12}
                  sm={6}
                  container
                  item
                  direction="row-reverse"
                  style={{ padding: '20px 10px 0 10px' }}
                >
                  {props.sellerList.length > 0 && (
                    <Pagination
                      count={Math.ceil(props.count / filtersValues.size)}
                      color="secondary"
                      page={filtersValues.page + 1}
                      onChange={handleChangePage}
                    />
                  )}
                </Grid>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
      <Dialog
        open={openConfirmDisable || openConfirmActive}
        onClose={handleClose}
        aria-labelledby="responsive-dialog-title"
      >
        <DialogTitle>
          <div
            style={{
              position: 'absolute',
              left: '3px',
              top: '10px',
              zIndex: 10,
            }}
          >
            {handleClose ? (
              <IconButton aria-label="close" onClick={handleClose}>
                <RonadIcons name="times" size={0.8}/>
              </IconButton>
            ) : null}
          </div>
          <div className={classes.cancelTitle}>
            {` آیا از 
          
          ${openConfirmDisable ? 'غیر' : ''}
          
          فعال کردن فروشنده با نام
          "${props.sellerList.find(seller => seller.id === id)?.name}"
          اطمینان دارید؟`}
          </div>
          <Divider style={{ width: '100%', margin: '16px 0  0' }} />
        </DialogTitle>
        <DialogActions>
          <Grid
            container
            spacing={2}
            justifyContent="center"
            style={{ textAlign: 'center', marginBottom: '8px' }}
          >
            <Grid item xs={12}>
              <Button
                autoFocus
                style={{ marginLeft: '8px' }}
                onClick={handleClose}
                variant="outlined"
                color="primary"
                size="large"
              >
                {`انصراف`}
              </Button>
              {openConfirmDisable ? (
                <Button
                  onClick={handleDisableSeller}
                  variant="contained"
                  size="large"
                  color="primary"
                >
                  {`غیرفعال کردن`}
                </Button>
              ) : (
                <Button
                  onClick={handleActiveSeller}
                  variant="contained"
                  size="large"
                  color="primary"
                >
                  {`فعال کردن`}
                </Button>
              )}
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>

      <Modal
        onClose={() => setOpenAddModal(false)}
        open={openAddModal}
        title={
          <div className={classes.addIcon}>
            <RonadIcons name='plus' size={3.5}  className='text-green'/>
          </div>
        }
        content={
          <Add
            onClose={() => setOpenAddModal(false)}
            count={props.count + 1}
            error={props.error}
            createSeller={handleCreateSeller}
          />
        }
      />
    </div>
  );
};

const mapStateToProps = () =>
  createStructuredSelector<Store, State>({
    sellerList: makeSelectSellersList(),
    count: makeSelectCountList(),
    inactiveCardCount: makeSelectInactiveCardCountList(),
    activeCardCount: makeSelectActiveCardCountList(),
    loading: makeSelectLoading(),
    error: makeSelectError(),
  });

function mapDispatchToProps(dispatch: Function) {
  return {
    sellersDispatch: (payload: Payload) => dispatch(getSellersAction(payload)),
    updateSellerStatusDispatch: (payload: {
      id: number;
      body: string;
      pagination: object;
    }) => dispatch(updateSellerStatus(payload)),
    createSellerDispatch: (payload: PayloadCreate) =>
      dispatch(createSellerAction(payload)),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(Sellers);
